import { PLATFORM_AUDIT_ACTION_OPTIONS } from './audit-log-actions';
import { platformAuditActionLabel } from '../dashboard/platform-audit-action-labels';

/**
 * Phase 12.7a — presentation rules for the Audit Logs page.
 *
 * Every rule here is a governed Owner decision, not a styling preference, so
 * each is named against the decision it implements.
 */

/** The nil actor 12.1C records for an unauthenticated event (§4). */
export const ANONYMOUS_ACTOR_ID = '00000000-0000-0000-0000-000000000000';

/** D-10 — what the Actor column shows when the actor is not resolvable. */
export const ANONYMOUS_ACTOR_LABEL = 'Anonymous / Unknown';

/**
 * **D-10.** A failed or blocked login has no authenticated user, so 12.1C stores
 * the nil UUID with `actorEmail: "unknown"`. Showing either raw value as the
 * user-facing identity would read as a real account, so both collapse to
 * `Anonymous / Unknown`.
 *
 * The nil UUID is NOT hidden — it stays available as the raw `actorId` in the
 * expanded row (D-3) so an investigator can still correlate precisely.
 */
export function auditActorLabel(entry: { actorId: string; actorEmail: string }): string {
  if (entry.actorId === ANONYMOUS_ACTOR_ID) return ANONYMOUS_ACTOR_LABEL;
  const email = entry.actorEmail?.trim();
  if (!email || email.toLowerCase() === 'unknown') return ANONYMOUS_ACTOR_LABEL;
  return email;
}

/**
 * **D-11 (table).** The application's local/user-facing convention — the same
 * `toLocaleString()` the Dashboard's Recent Activity already uses, so one event
 * reads identically on both surfaces.
 */
export function auditTimestampLabel(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/**
 * **D-11 (expanded row).** The exact underlying value, so an investigator can
 * correlate against logs and other systems. Stored timestamps are never altered;
 * this is display only.
 */
export function auditExactTimestamp(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toISOString();
}

/** D-2 — `Resource ID` renders an em dash when null, never an empty cell. */
export const EMPTY_VALUE = '—';

export function resourceIdLabel(resourceId: string | null): string {
  return resourceId === null || resourceId === '' ? EMPTY_VALUE : resourceId;
}

/**
 * A half-open `[from, to)` range from two `<input type="date">` values (D-1).
 *
 * The end date is advanced by one day so that picking the same day for both
 * bounds selects that whole day. This is why the backend takes `to` as
 * EXCLUSIVE — it removes the `23:59:59.999` workaround entirely.
 */
export function toIsoRange(fromDate: string, toDate: string): { from?: string; to?: string } {
  const out: { from?: string; to?: string } = {};
  if (fromDate) {
    const d = new Date(`${fromDate}T00:00:00.000Z`);
    if (!Number.isNaN(d.getTime())) out.from = d.toISOString();
  }
  if (toDate) {
    const d = new Date(`${toDate}T00:00:00.000Z`);
    if (!Number.isNaN(d.getTime())) {
      d.setUTCDate(d.getUTCDate() + 1);
      out.to = d.toISOString();
    }
  }
  return out;
}

export { PLATFORM_AUDIT_ACTION_OPTIONS, platformAuditActionLabel };
